// Types for finished-product (PT) transfers between WMS bodegas (B1 <-> B2).
// Producto Terminado stock lives in the WMS model (wms_inventory at positions
// inside warehouses), NOT in per-plant ingredient tables. A PT transfer moves
// boxes of ONE product+lote between bodegas via the create_product_transfer RPC.
// It's a pure relocation: net-zero on products.stock. See ingredientTransfer.ts
// for the sibling P1<->P2 ingredient kind.

import { z } from "zod";

import type { Database } from "~/types/supabase";

// Derive DB row shape from the generated types (single source of truth).
export type ProductTransferRow =
  Database["public"]["Tables"]["product_transfers"]["Row"];

// Which arm of get_product_lotes_for_transfer a lote row came from:
//  'bodega'        -> ingested wms_inventory stock (warehouse_id = uuid)
//  'plant_pallets' -> un-ingested finished PALLETS of a still-open batch (transfer by pallet)
//  'plant_pending' -> un-ingested PENDING ingestion of a completed batch (transfer by boxes)
export type ProductLoteSource = "bodega" | "plant_pallets" | "plant_pending";

// A lote available to transfer (one row per location+lote), from the
// get_product_lotes_for_transfer RPC. The `source` column is added after the last
// supabase types regeneration, so intersect it in explicitly.
export type ProductLoteAvailability =
  Database["public"]["Functions"]["get_product_lotes_for_transfer"]["Returns"][number] & {
    source: ProductLoteSource;
  };

// A finished ("Listo") pallet available to transfer, from get_batch_ready_pallets.
export interface ReadyPallet {
  id: string;
  sequence_number: number | null;
  code: string;
  current_box_count: number;
  production_ended_at: string | null;
  lot_number: string | null;
}

// A bodega (WMS warehouse) option for the origin/destination selects.
export interface WarehouseOption {
  id: string;
  code: string;
  name: string;
}

// Two-step transfer lifecycle: stock leaves origin (in_transit) and only lands in
// the destination once the receiving bodega confirms (completed). cancelled returns
// the stock to origin.
export type ProductTransferStatus = "in_transit" | "completed" | "cancelled";

// Read model: a PT transfer header with joined names for the history list.
// The receive/cancel tracking columns are added by the transitorio migration; they
// are declared here explicitly so the read model has them regardless of when the
// generated Row type is regenerated.
export interface ProductTransferWithNames extends ProductTransferRow {
  received_by?: string | null;
  received_at?: string | null;
  cancelled_by?: string | null;
  cancelled_at?: string | null;
  product?: {
    id: string;
    short_name: string;
    sku?: string | null;
    base_measurement_unit?: string | null;
    unit_code?: string | null;
  } | null;
  from_warehouse?: { id: string; code: string; name: string } | null;
  to_warehouse?: { id: string; code: string; name: string } | null;
  production_batch?: { batch_number: string; lot_number: string | null } | null;
  performed_by_profile?: {
    id: string;
    name: string | null;
    lastname: string | null;
  } | null;
  received_by_profile?: {
    id: string;
    name: string | null;
    lastname: string | null;
  } | null;
}

// Read model: a finished-good between-plants transfer (P1<->P2<->NC, TF-#####) with
// joined product + performer names, for the Traslados history list. These relocate
// un-ingested produced stock and live in fg_plant_transfers — a separate table from
// the bodega product_transfers. Not in the generated Database types yet, so the row
// shape is declared explicitly.
export interface FgPlantTransferWithNames {
  id: string;
  transfer_number: string;
  product_id: string | null;
  production_batch_id: string | null;
  lot_number: string | null;
  from_plant: string;
  to_plant: string;
  boxes: number;
  notes: string | null;
  performed_by: string | null;
  created_at: string;
  product?: {
    id: string;
    short_name: string;
    sku?: string | null;
    base_measurement_unit?: string | null;
    unit_code?: string | null;
  } | null;
  performed_by_profile?: {
    id: string;
    name: string | null;
    lastname: string | null;
  } | null;
}

// Return value of the create_product_transfer RPC.
export interface CreateProductTransferResult {
  id: string;
  transfer_number: string;
  status?: ProductTransferStatus;
}

// Return value of the receive/cancel RPCs.
export interface ProductTransferActionResult {
  id: string;
  transfer_number: string;
  status: ProductTransferStatus;
}

// Header + lines for the printable internal transfer document (guía de traslado
// interno). Vocabulary-neutral so it serves every traslado kind: producto terminado
// (bodega B1/B2 or plant P1/P2, one line) and ingredients (P1/P2, one line per item).
export interface InternalTransferGuideData {
  transferNumber: string;
  transferredAt: string;
  fromLabel: string;
  toLabel: string;
  performedBy: string;
  notes: string | null;
  lines: {
    codigo: string;
    item: string;
    um: string;
    lote: string;
    cantidad: number;
  }[];
}

// Payload for the create_product_transfer RPC (service layer).
export interface CreateProductTransferInput {
  fromWarehouse: string;
  toWarehouse: string;
  productId: string;
  boxes: number;
  lotNumber: string | null;
  productionBatchId: string | null;
  notes?: string | null;
  performedBy: string;
}

// Payload for the update_product_transfer RPC: edit an in-transit guía's
// quantity + notes (product/lote/warehouses are fixed).
export interface UpdateProductTransferInput {
  transferId: string;
  boxes: number;
  notes?: string | null;
  performedBy: string;
}

// Payload for the transfer_fg_between_plants RPC: relocate a batch's un-ingested
// (pending) finished-good stock between plants (P1<->P2<->NC). Unlike a bodega
// transfer it touches no wms_inventory; it only moves pending-ingestion quantity
// and is net-zero on products.stock.
export interface FgPlantTransferInput {
  productionBatchId: string;
  fromPlant: string;
  toPlant: string;
  boxes: number;
  notes?: string | null;
  performedBy: string;
}

export interface FgPlantTransferResult {
  id: string;
  transfer_number: string;
  production_batch_id: string;
  from_plant: string;
  to_plant: string;
  boxes: number;
}

// Payload for the transfer_fg_pallets_between_plants RPC: relocate whole finished
// PALLETS of a still-open production batch between plants (P1<->P2<->NC) — the
// partial-during-production traslado. Net-zero on products.stock / wms_inventory.
export interface FgPalletTransferInput {
  productionBatchId: string;
  fromPlant: string;
  toPlant: string;
  palletIds: string[];
  notes?: string | null;
  performedBy: string;
}

export interface FgPalletTransferResult extends FgPlantTransferResult {
  pallets: number;
}

// -----------------------------------------------------------------------------
// Form schema (react-hook-form + zodResolver)
// -----------------------------------------------------------------------------

export const createProductTransferSchema = z
  .object({
    from_warehouse: z.string().min(1, "Selecciona la planta de origen"),
    to_warehouse: z.string().min(1, "Selecciona la planta de destino"),
    product_id: z.string().uuid("Selecciona un producto"),
    // display-only labels carried by the form (not sent to the backend)
    product_name: z.string(),
    lot_number: z.string().nullable(),
    production_batch_id: z.string().uuid().nullable(),
    lote_label: z.string(),
    boxes: z.coerce
      .number()
      .int("Ingresa un número entero")
      .positive("La cantidad debe ser mayor a 0"),
    // display-only max carried by the form for the "exceeds stock" refine
    available: z.coerce.number(),
    notes: z.string().optional(),
  })
  .refine((v) => v.from_warehouse !== v.to_warehouse, {
    message: "La planta de origen y destino no pueden ser iguales",
    path: ["to_warehouse"],
  })
  .refine((v) => v.boxes <= v.available, {
    message: "La cantidad excede el stock disponible en la planta de origen",
    path: ["boxes"],
  });

export type CreateProductTransferFormData = z.infer<
  typeof createProductTransferSchema
>;
